import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import toast from 'react-hot-toast'
import useLoggedUser from '../hooks/useLoggedUser'
import { useApi } from '../hooks/useApi'

const PASSWORD_ERROR_MSG =
  'Password must be min 8 chars, with at least a symbol, upper and lower case letters, and a number'

export default function UserPage() {
  const [activeTab, setActiveTab] = useState('overview')
  const navigate = useNavigate()
  const { tab } = useParams()

  useEffect(() => {
    if (tab) setActiveTab(tab)
  }, [tab])

  const handleTabClick = (t: string) => {
    setActiveTab(t)
    navigate(`/dashboard/user/${t}`)
  }

  return (
    <Wrap>
      <PageTitle>Your account</PageTitle>
      <TabBar>
        <TabBtn active={activeTab === 'overview'} onClick={() => handleTabClick('overview')}>Overview</TabBtn>
      </TabBar>
      <Content>{activeTab === 'overview' && <Overview />}</Content>
    </Wrap>
  )
}

const Overview = () => {
  const { callApi } = useApi()
  const [password, setPassword] = useState<string | null>(null)
  const [confirmPassword, setConfirmPassword] = useState<string | null>(null)
  const { data: me } = useLoggedUser()

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (password !== confirmPassword) {
      toast.error('Both passwords must match')
      return
    }
    try {
      await callApi(`/admin/users/${me?._id}`, { ...me, password }, 'PUT')
      toast.success('Password updated successfully')
    } catch {
      toast.error('Failed to update password')
    }
  }

  return (
    <FormWrap>

      <FormCard>
        <FormTitle>Change password{me?.email && <> — <EmailText>{me.email}</EmailText></>}</FormTitle>
        <Form onSubmit={handleFormSubmit}>
          <Field>
            <FieldLabel>New Password</FieldLabel>
            <FieldInput
              type="password"
              value={password ?? ''}
              onChange={(e) => setPassword(e.target.value)}
              pattern="^(?=.*\d)(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*[A-Z]).{8,}$"
              required
              title={PASSWORD_ERROR_MSG}
              placeholder="••••••••"
            />
          </Field>
          <Field>
            <FieldLabel>Confirm New Password</FieldLabel>
            <FieldInput
              type="password"
              value={confirmPassword ?? ''}
              onChange={(e) => setConfirmPassword(e.target.value)}
              pattern="^(?=.*\d)(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*[A-Z]).{8,}$"
              required
              title={PASSWORD_ERROR_MSG}
              placeholder="••••••••"
            />
          </Field>
          <SubmitBtn type="submit">Update password</SubmitBtn>
        </Form>
      </FormCard>
    </FormWrap>
  )
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`

const PageTitle = styled.h1`
  font-size: 20px;
  font-weight: 600;
  color: #ededed;
  margin: 0 0 20px;
`

const TabBar = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  margin-bottom: 24px;
`

const TabBtn = styled.button<{ active: boolean }>`
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 500;
  background: none;
  border: none;
  border-bottom: 2px solid ${({ active }) => active ? '#6f5ce6' : 'transparent'};
  margin-bottom: -1px;
  color: ${({ active }) => active ? '#a89aff' : '#555'};
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;

  &:hover { color: ${({ active }) => active ? '#a89aff' : '#a0a0a0'}; }
`

const Content = styled.div``

const FormWrap = styled.div`
  max-width: 400px;
  padding-top: 8px;
`

const FormCard = styled.div`
  background: #111;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 8px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const FormTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #ededed;
  margin: 0;
`

const EmailText = styled.span`
  color: #777;
  font-weight: 400;
`

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const FieldLabel = styled.label`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #777;
`

const FieldInput = styled.input`
  background: #0d0d0d;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  color: #ededed;
  font-size: 13px;
  padding: 0 12px;
  height: 34px;
  outline: none;
  transition: border-color 0.15s;

  &::placeholder { color: #777; }
  &:focus { border-color: rgba(111, 92, 230, 0.5); }
`

const SubmitBtn = styled.button`
  height: 34px;
  padding: 0 16px;
  background: rgba(111, 92, 230, 0.15);
  border: 1px solid rgba(111, 92, 230, 0.35);
  border-radius: 6px;
  color: #a89aff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  align-self: flex-start;
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    background: rgba(111, 92, 230, 0.25);
    border-color: rgba(111, 92, 230, 0.5);
  }
`
